import { locationColorIndex } from "@/lib/timeline/location/LocationMarker/palette";
import { useEffect, useMemo, useRef } from "react";

import type { ChapterBoard } from "@/lib/timeline/board";
import { sampleBoard } from "@/lib/timeline/board";
import { locationRunsForMount } from "@/lib/timeline/location/LocationMarker";
import type { BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import type { PlaybackState } from "@/lib/timeline/playback";
import { applyBoardTimelineOverlays } from "@/lib/timeline/scene/TimelineFrame";
import type { TimelineViewTransform } from "@/lib/timeline/view/ViewTransform/transform";
import {
  boardCharacterDebugFrames,
  boardLocationStack,
  boardMarkerDisc,
} from "./board/BoardTimelineOverlayParts";
import { timelineFrameDetail } from "./timelineFrameDetail";
import { TimelinePovIndicatorLayer } from "./TimelinePovIndicatorLayer";

interface BoardTimelineOverlaysProps {
  canvas: HTMLCanvasElement | null;
  board: ChapterBoard;
  layout: BookTimelineLayout;
  getPlayback: () => PlaybackState;
  getViewportWidth: () => number;
  getViewportHeight: () => number;
  getViewTransform: () => TimelineViewTransform;
  boardModelDebug: boolean;
}

export function BoardTimelineOverlays({
  canvas,
  board,
  layout,
  getPlayback,
  getViewportWidth,
  getViewportHeight,
  getViewTransform,
  boardModelDebug,
}: BoardTimelineOverlaysProps) {
  const characterRef = useRef<HTMLDivElement>(null);
  const locationRef = useRef<HTMLDivElement>(null);
  const castById = useMemo(
    () => new Map(board.cast.map((member) => [member.characterId, member])),
    [board.cast],
  );
  const mountRuns = useMemo(() => locationRunsForMount(layout), [layout]);

  useEffect(() => {
    if (canvas === null || characterRef.current === null || locationRef.current === null) {
      return;
    }
    const characterRoot = characterRef.current;
    const locationRoot = locationRef.current;

    const paintAtPlayhead = (playheadMs: number, viewportWidth: number): void => {
      applyBoardTimelineOverlays(
        characterRoot,
        locationRoot,
        board,
        layout,
        sampleBoard(board, playheadMs),
        viewportWidth,
        getViewportHeight(),
        getViewTransform(),
        boardModelDebug,
      );
    };

    const onFrame = (event: Event): void => {
      const detail = timelineFrameDetail(event);
      if (detail?.boardSample === undefined || detail.viewportWidth === undefined) {
        return;
      }
      applyBoardTimelineOverlays(
        characterRoot,
        locationRoot,
        board,
        layout,
        detail.boardSample,
        detail.viewportWidth,
        getViewportHeight(),
        getViewTransform(),
        boardModelDebug,
      );
    };

    canvas.addEventListener("timeline-frame", onFrame);
    paintAtPlayhead(getPlayback().playheadMs, getViewportWidth());
    return () => canvas.removeEventListener("timeline-frame", onFrame);
  }, [
    board,
    boardModelDebug,
    canvas,
    getPlayback,
    getViewportHeight,
    getViewTransform,
    getViewportWidth,
    layout,
  ]);

  return (
    <>
      <div ref={characterRef} className="timeline-labels" aria-hidden="true">
        <div className="timeline-marker-layer timeline-marker-layer--discs">
          {board.characterMarkers.map((marker) =>
            boardMarkerDisc(marker.characterId, castById, layout),
          )}
        </div>
        {boardModelDebug ? (
          <div
            className="timeline-marker-layer timeline-marker-layer--debug-markers"
            data-character-marker-debug-host=""
            aria-hidden="true"
          >
            {board.cast.flatMap((member) =>
              boardCharacterDebugFrames(member.characterId),
            )}
          </div>
        ) : null}
        <div
          className="timeline-marker-layer timeline-marker-layer--debug-groups"
          data-character-group-debug-host=""
          aria-hidden="true"
        />
        <TimelinePovIndicatorLayer />
        <div className="timeline-marker-layer timeline-marker-layer--names">
          {board.cast.map((member) => (
            <span
              key={member.characterId}
              data-marker-part="name"
              data-character-id={member.characterId}
              className="timeline-marker__name"
            >
              {member.displayName}
            </span>
          ))}
        </div>
      </div>
      <div ref={locationRef} className="timeline-locations" aria-hidden="true">
        {mountRuns.map((run, index) =>
          boardLocationStack(run, locationColorIndex(mountRuns, index), board, boardModelDebug),
        )}
        {boardModelDebug ? (
          <>
            <div
              className="timeline-location-cast-debug-host"
              data-location-cast-debug-host=""
              aria-hidden="true"
            />
            <div
              className="timeline-board-debug-label-host"
              data-board-model-debug-label-host=""
              aria-hidden="true"
            />
          </>
        ) : null}
      </div>
    </>
  );
}
